import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "../../auth/AuthProvider";
import { Icon } from "../../components/icons";
import { LogViewer } from "../../components/LogViewer";
import { Pagination } from "../../components/Pagination";
import {
  Alert,
  Card,
  CardHeader,
  DeploymentBadge,
  EmptyState,
  LoadingBlock,
  cx,
} from "../../components/ui";
import { absoluteTime, duration, relativeTime } from "../../lib/format";
import { useScheduleRunLogs, useScheduleRuns } from "../../lib/queries";
import type { ScheduleRun } from "../../lib/types";

/** Runs per page. A job at the five-minute floor makes 288 a day, so pages. */
const PER_PAGE = 20;

/**
 * What this job has actually done, from the engine's own record of each run.
 *
 * The question this answers is the one the platform could not answer before:
 * a customer whose cron did not do what they expected saw a row saying
 * "enabled" and nothing else. One list, newest first, and the selected run's
 * output beneath it — beneath rather than beside, because the schedule page is
 * a single column of cards and a two-pane layout inside one of them reads as a
 * different page.
 *
 * Output is `developer` and above, matching app and database logs: a command
 * prints whatever it prints, and that regularly includes tokens. A viewer sees
 * that a run happened and how it went, which is the part with no secret in it.
 */
export function ScheduleRuns({
  orgId,
  scheduleId,
  provisioned,
}: {
  orgId: string | undefined;
  scheduleId: string | undefined;
  /** A job the engine never confirmed has no history to ask for. */
  provisioned: boolean;
}) {
  const { t } = useTranslation("schedules");
  const canReadOutput = useCan("developer");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isPending, error } = useScheduleRuns(orgId, scheduleId, {
    limit: PER_PAGE,
    offset: (page - 1) * PER_PAGE,
  });

  const runs = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader
        title={t("runs.title")}
        description={
          total > runs.length ? t("runs.leadCount", { count: total }) : t("runs.lead")
        }
      />

      {isPending ? (
        <div className="p-5">
          <LoadingBlock label={t("runs.loading")} />
        </div>
      ) : error ? (
        <div className="p-5">
          <Alert>{error.message}</Alert>
        </div>
      ) : data?.unavailable ? (
        /* Not the same as no runs, and saying "none" here would send somebody
           to debug a job that ran perfectly well. */
        <div className="p-5">
          <Alert tone="warning">{t("runs.unavailable")}</Alert>
        </div>
      ) : runs.length === 0 ? (
        <EmptyState
          icon="clock"
          title={provisioned ? t("runs.empty") : t("runs.notCreated")}
          description={provisioned ? t("runs.emptyLead") : t("runs.notCreatedLead")}
        />
      ) : (
        <>
          {/* An accordion rather than a pane: the output belongs under the run
              it came from, and a detail panel below the whole list left the
              selected row and its log at opposite ends of a long card. */}
          <ul className="divide-y divide-line">
            {runs.map((run) => (
              <li key={run.id}>
                <RunRow
                  run={run}
                  selectable={canReadOutput}
                  selected={run.id === selectedId}
                  onSelect={() => setSelectedId(run.id === selectedId ? null : run.id)}
                />
                {run.id === selectedId && canReadOutput && (
                  <div className="space-y-4 border-t border-line bg-sunken/40 p-5">
                    <RunFacts run={run} />
                    <RunLog orgId={orgId} scheduleId={scheduleId} run={run} />
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Paged rather than a growing list: a job at the five-minute floor
              records 288 runs a day, and somebody reading down them needs a
              way back as much as a way on. Renders nothing on one page. */}
          <Pagination
            total={total}
            perPage={PER_PAGE}
            page={page}
            onChange={(next) => {
              setPage(next);
              // The open run is on the page being left, and an accordion that
              // stays open over different rows is a bug that looks like one.
              setSelectedId(null);
            }}
          />

          {!canReadOutput && (
            <p className="border-t border-line px-5 py-3 text-[13px] text-fg-subtle">
              {t("runs.readOnly")}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * One run. The status leads because it is the only thing being scanned for;
 * the time and how long it took follow on a quieter line.
 */
function RunRow({
  run,
  selectable,
  selected,
  onSelect,
}: {
  run: ScheduleRun;
  selectable: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const body = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <DeploymentBadge status={run.status} />
        <span className="min-w-0 truncate text-sm text-fg" title={absoluteTime(run.createdAt)}>
          {relativeTime(run.createdAt)}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-fg-subtle">
        <span className="tabular-nums">{duration(run.startedAt, run.finishedAt)}</span>
        {selectable && (
          <Icon
            name="chevron"
            className={cx(
              "size-3.5 transition-transform duration-200 ease-smooth",
              selected && "rotate-90",
            )}
          />
        )}
      </div>
    </>
  );

  if (!selectable) {
    return <div className="flex items-center justify-between gap-3 px-5 py-3">{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-expanded={selected}
      className={cx(
        "flex w-full items-center justify-between gap-3 border-l-2 py-3 pr-5 pl-[1.125rem] text-left",
        "transition-colors duration-200 ease-smooth",
        selected ? "border-l-accent bg-raised" : "border-l-transparent hover:bg-raised",
      )}
    >
      {body}
    </button>
  );
}

function RunFacts({ run }: { run: ScheduleRun }) {
  const { t } = useTranslation("schedules");
  return (
    <>
      {/* No outcome here: the row's badge is a few pixels above it, and two
          vocabularies for one fact on one screen is worse than one. */}
      <dl className="flex flex-wrap gap-x-10 gap-y-3">
        <Fact label={t("runs.started")}>{absoluteTime(run.startedAt ?? run.createdAt)}</Fact>
        <Fact label={t("runs.duration")}>{duration(run.startedAt, run.finishedAt)}</Fact>
      </dl>
      {run.errorMessage && (
        <p className="border-l-2 border-l-danger pl-3 text-sm text-fg-muted">{run.errorMessage}</p>
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="micro">{label}</dt>
      <dd className="mt-1 text-sm text-fg tabular-nums">{children}</dd>
    </div>
  );
}

function RunLog({
  orgId,
  scheduleId,
  run,
}: {
  orgId: string | undefined;
  scheduleId: string | undefined;
  run: ScheduleRun;
}) {
  const { t } = useTranslation("schedules");
  const { data, isPending, error } = useScheduleRunLogs(orgId, scheduleId, run.id);

  if (isPending) return <LoadingBlock label={t("runs.loadingOutput")} />;
  if (error) return <Alert>{error.message}</Alert>;

  return (
    <LogViewer
      logs={data?.logs ?? ""}
      streaming={Boolean(data && !data.complete)}
      downloadName={`schedule-run-${run.id}`}
      emptyMessage={run.hasLogs ? t("runs.printedNothing") : t("runs.outputGone")}
    />
  );
}
